import React, { useContext, useEffect, useState } from "react";
import { Box, Button, IconButton, useTheme } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import Background from "../components/Background";
import TitledBackground from "../components/TitledBackground";
import VerticalList from "../components/VerticalList";
import ThemeToggle from "../components/ThemeToggle";
import AccentToggle from "../components/AccentToggle";
import { useColourMode } from "../contexts/ColourContext";
import { hasAccess, formatPhoneNumber } from "../functions";
import { prodApi } from "../data/api";

const url = prodApi.user;

const UpdateInfo = ({ title, userId, firstName, lastName, phone }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    firstName: firstName || "",
    lastName: lastName || "",
    phone: phone || "",
    image: null,
  });

  const colourTheme = useTheme();

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB

    // Function to compress image
    const compressImage = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;

          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const maxWidth = 800; // Max width for compressed image
            const maxHeight = 800; // Max height for compressed image
            let width = img.width;
            let height = img.height;

            // Scale down the image while maintaining aspect ratio
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width *= ratio;
              height *= ratio;
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const compressedFile = new File([blob], file.name, {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                } else {
                  reject(new Error("Compression failed"));
                }
              },
              "image/jpeg",
              0.7 // Compression quality (0.0 - 1.0)
            );
          };

          img.onerror = () => reject(new Error("Failed to load image"));
        };

        reader.onerror = () => reject(new Error("Failed to read file"));
      });
    };

    // Check and compress if file is larger than maxSize
    if (file.size > maxSize) {
      compressImage(file)
        .then((compressedFile) => {
          setFormData({ ...formData, image: compressedFile });
          alert("Image was compressed and uploaded successfully.");
        })
        .catch((err) => {
          console.error("Image compression failed:", err);
          alert("Failed to compress image. Please try again.");
        });
    } else {
      setFormData({ ...formData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("firstName", formData.firstName || "");
      formDataToSend.append("lastName", formData.lastName || "");
      formDataToSend.append("phone", formData.phone || "");
      if (formData.image) {
        formDataToSend.append("profilePic", formData.image);
      }

      await axios.put(`${url}/updateinfo/${userId}`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Data updated successfully, log in again for updated changes");
      setIsExpanded(false);
      window.location.reload();
    } catch (error) {
      console.error("Error updating data:", error);
      // alert("Failed to update data. Please try again.");
    }
  };

  return (
    <Box onClick={toggleExpand} sx={{ cursor: "pointer", width: "100%" }}>
      <Box>{title}</Box>
      {isExpanded && (
        <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "column",
              gap: "0.125rem",
              fontSize: "0.9rem",
            }}
          >
            {/* First Name */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "0.5rem",
              }}
            >
              <label>First Name:</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="First Name"
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "70%",
                  padding: "0.5rem",
                  borderRadius: "5px",
                  background: "transparent",
                  border: `1px solid ${colourTheme.palette.text.primary}`,
                  color: colourTheme.palette.text.primary,
                }}
              />
            </Box>

            {/* Last Name */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "0.5rem",
              }}
            >
              <label>Last Name:</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Last Name"
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "70%",
                  padding: "0.5rem",
                  borderRadius: "5px",
                  background: "transparent",
                  border: `1px solid ${colourTheme.palette.text.primary}`,
                  color: colourTheme.palette.text.primary,
                }}
              />
            </Box>

            {/* Phone Number */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "0.5rem",
              }}
            >
              <label>Phone Number:</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone Number"
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "70%",
                  padding: "0.5rem",
                  borderRadius: "5px",
                  background: "transparent",
                  border: `1px solid ${colourTheme.palette.text.primary}`,
                  color: colourTheme.palette.text.primary,
                }}
              />
            </Box>

            {/* Image Upload */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "0.5rem",
              }}
            >
              <label>Profile Image:</label>
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: "70%",
                  padding: "0.5rem",
                  border: `1px solid ${colourTheme.palette.text.primary}`,
                  color: colourTheme.palette.text.primary,
                  borderRadius: "5px",
                  fontSize: "0.9rem",
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                m: "1.5rem 0",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ flex: 1, mr: 1, fontSize: "0.65rem" }}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ flex: 1, fontSize: "0.65rem" }}
                onClick={toggleExpand}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Box>
  );
};

const PasswordInput = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  colourTheme,
  symbolSize,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mt: "0.5rem",
      }}
    >
      <label>{label}</label>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "50%",
          position: "relative",
        }}
      >
        <input
          type={showPassword ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "0.5rem",
            borderRadius: "5px",
            background: "transparent",
            border: `1px solid ${colourTheme.palette.text.primary}`,
            color: colourTheme.palette.text.primary,
          }}
        />
        <IconButton
          onClick={togglePasswordVisibility}
          sx={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            color: colourTheme.palette.text.primary,
          }}
        >
          {showPassword ? (
            <VisibilityOff sx={{ fontSize: symbolSize }} />
          ) : (
            <Visibility sx={{ fontSize: symbolSize }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

const UpdatePassword = ({ userId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");

  const colourTheme = useTheme();
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    setError(""); // Clear errors on toggle
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      await axios.put(
        `https://www.data.banilux.ng/user/changepassword/${userId}`,
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert("Password updated successfully!");
      setIsExpanded(false);
    } catch (error) {
      console.error("Error updating password:", error);
      const message =
        error.response && error.response.status === 400
          ? "Invalid password."
          : "Failed to update password. Please try again.";
      setError(message);
    }
  };

  return (
    <Box onClick={toggleExpand} sx={{ cursor: "pointer", width: "100%" }}>
      <Box>Change Password</Box>
      {isExpanded && (
        <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "column",
              gap: "0.125rem",
              fontSize: "0.9rem",
            }}
          >
            {/* Current Password */}
            <PasswordInput
              label="Current Password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleInputChange}
              placeholder="Enter Password"
              colourTheme={colourTheme}
              symbolSize="1.25rem"
            />

            {/* New Password */}
            <PasswordInput
              label="New Password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder="Enter New Password"
              colourTheme={colourTheme}
              symbolSize="1.25rem"
            />

            {/* Confirm Password */}
            <PasswordInput
              label="Confirm Password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm New Password"
              colourTheme={colourTheme}
              symbolSize="1.25rem"
            />

            {/* Error Message */}
            {error && (
              <Box
                sx={{
                  color: "red",
                  fontSize: "0.75rem",
                  mt: "0.5rem",
                  textAlign: "center",
                }}
              >
                {error}
              </Box>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                m: "1.5rem 0",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ flex: 1, mr: 1, fontSize: "0.65rem" }}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ flex: 1, fontSize: "0.65rem" }}
                onClick={toggleExpand}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Box>
  );
};

const Settings = ({ maxWidth }) => {
  const [user, setUser] = useState({});
  const [userLoaded, setUserLoaded] = useState(false);
  const [error, setError] = useState("");
  const { loggedIn, isLoggedIn } = useContext(AuthContext);
  const { accentColour } = useColourMode();

  const userId = isLoggedIn.uniqueId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          setError("Authorization token is missing.");
          return;
        }

        if (userLoaded) {
          // Data is already loaded; no need to fetch again
          return;
        }

        const response = await axios.get(`${url}/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Process and store the users' data
        setUser(response.data);
        setUserLoaded(true);
      } catch (error) {
        console.error("Error fetching users data:", error);
        const message =
          error.response && error.response.status === 403
            ? "You do not have the necessary permissions."
            : "Failed to fetch project data. Please try again later.";
        setError(message);
      }
    };

    fetchData();
  }, [userLoaded]);

  return (
    <>
      {loggedIn &&
        hasAccess(isLoggedIn.accessLevel, [
          "admin",
          "user",
          "data",
          "omni",
        ]) && (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                minHeight: "110dvh",
                width: "90%",
                maxWidth: maxWidth,
                zIndex: 0,
                m: "auto",
                p: "auto",
              }}
            >
              <Box
                sx={{
                  mt: 9,
                  mb: 1.5,
                  fontSize: "2rem",
                  fontWeight: "300",
                }}
              >
                Settings
              </Box>
              <Background>
                <Box
                  sx={{
                    display: "flex",
                    height: "80px",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={isLoggedIn.image}
                    alt={isLoggedIn.firstName}
                    style={{
                      borderRadius: "50%",
                      height: "100%",
                      aspectRatio: "1/1",
                      marginLeft: "0.5rem",
                      objectFit: "cover",
                    }}
                  />
                  <Box sx={{ marginLeft: "1rem", lineHeight: "1.1rem" }}>
                    <Box sx={{ fontSize: "1.2rem" }}>
                      {isLoggedIn.firstName} {isLoggedIn.lastName}
                    </Box>
                    <Box sx={{ fontSize: "0.75rem", color: `${accentColour}` }}>
                      {isLoggedIn.accessLevel.toUpperCase()}
                    </Box>
                  </Box>
                </Box>
              </Background>
              <TitledBackground title="PERSONAL INFORMATION">
                <VerticalList
                  items={[
                    isLoggedIn.firstName,
                    isLoggedIn.lastName,
                    isLoggedIn.username,
                    formatPhoneNumber(isLoggedIn.phone),
                  ]}
                />
              </TitledBackground>
              <TitledBackground title="PRIVACY AND SECURITY">
                {userLoaded ? (
                  <VerticalList
                    items={[
                      <UpdateInfo
                        key="update-info"
                        title="Change Personal Information"
                        firstName={user.firstName || ""}
                        lastName={user.lastName || ""}
                        phone={user.phone || ""}
                        userId={user.uniqueId || ""}
                      />,
                      <UpdatePassword key="update-password" userId={userId} />,
                      // "Change Password",
                    ]}
                  />
                ) : (
                  <Box>Loading user data...</Box>
                )}
              </TitledBackground>
              <TitledBackground title="APPEARANCE">
                <ThemeToggle />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "0.9rem",
                  }}
                >
                  Accents
                  <AccentToggle />
                </Box>
              </TitledBackground>
            </Box>
            <Box sx={{ height: "10dvh" }} />
          </Box>
        )}
    </>
  );
};

export default Settings;
